<script setup lang="ts">
const emit = defineEmits([
  "close",
  "get2FA",
  "getQueue2FA",
  "toggleModalLock",
  "toggleQueueModal",
  "isPurchased",
  "isUpdated",
  "isLocked",
  "mintError",
]);
const { queueConfirmTime } = useRuntimeConfig().public;
const queueMaxConfirmTime = Number(queueConfirmTime) || 20;
const { cancelQueueTicket } = useQueuesController();

const props = defineProps({
  baseMarketItem: {
    type: Object as PropType<BaseMarketItem>,
    required: true,
  },
  createPurchase: {
    type: Function as PropType<
      ItemController["create"] | CollectibleController["create"]
    >,
    required: true,
  },
  identifier: {
    type: Object as PropType<
      ItemIdentifier | CollectibleIdentifier | MintIdentifier
    >,
    required: true,
  },
  minting: {
    type: Boolean,
    required: false,
    default: false,
  },
  isSoldOut: {
    type: Boolean,
    required: false,
    default: false,
  },
  buyFromQueue: {
    type: Boolean,
    required: false,
    default: false,
  },
  isLimitedEdition: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const { t } = useI18n();
const { toaster } = useToaster();
const { getEstimatedGasFees } = useTransactionsController();
const GFALExStore = useGFALExStore();
const { trackEvent } = useGAnalytics();
const userStore = useUserStore();
const isLoading = ref<boolean>(false);
const isProcessingPurchase = ref<boolean>(false);
const gasFeesGFAL = ref<number>(0);
const gasFeesCovered = ref<boolean>(false);
const gasFeesReason = ref<string | null>(null);
const isKYCVerified = ref<boolean>(true);
const KYC_URI = ref<string>("");
const errorMessage = ref<string>("");
const isModalLoaderShown = ref<boolean>(false);
const queueRemainingSeconds = ref<number>(queueMaxConfirmTime);
let queueTimerInterval: ReturnType<typeof setInterval> | undefined = undefined;

const handleSoldOut = () => {
  errorMessage.value = t("errorMessages.itemAlreadySold");
  isProcessingPurchase.value = false;
  isModalLoaderShown.value = false;
  emit("toggleModalLock");
};

const confirmButtonText = computed(() => {
  const baseText = t("main.modalBuyBegin.confirmPay");
  if (props.buyFromQueue) {
    return `${baseText} (${queueRemainingSeconds.value})`;
  }
  return baseText;
});

onMounted(async () => {
  if (props.isSoldOut) handleSoldOut();
  isLoading.value = true;
  await getGasFees();
  isLoading.value = false;
  if (props.buyFromQueue) {
    queueTimerInterval = setInterval(() => {
      if (queueRemainingSeconds.value > 0) {
        queueRemainingSeconds.value--;
      } else {
        if (queueTimerInterval) clearInterval(queueTimerInterval);
        cancelPurchaseFromQueue();
      }
    }, 1000);
  }
});

onUnmounted(() => {
  if (queueTimerInterval) {
    clearInterval(queueTimerInterval);
  }
});

const cancelFromButton = () => {
  trackEvent("cancel_buy_button");
  emit("close");
  if (props.buyFromQueue) {
    cancelPurchaseFromQueue();
    window.location.reload();
  }
};

const showInfluencerReferral = () => {
  const { excludedInfluencers } = useRuntimeConfig().public;
  const excluded = excludedInfluencers.split(",").filter((id) => id !== "");
  if (userStore.referredByUser === "" || userStore.referredByUser == null)
    return false;
  if (excluded.includes(userStore.referredByUser)) return false;
  const expiryDate = new Date(userStore.creationTime);
  expiryDate.setMonth(expiryDate.getMonth() + 3);
  return Date.now() <= expiryDate.getTime();
};

const getGasFees = async () => {
  try {
    const gasResponse = await getEstimatedGasFees(TransactionType.PURCHASE);
    if (!gasResponse) throw new Error("No gas fees response");
    gasFeesGFAL.value = gasResponse?.amount ?? 0;
    gasFeesCovered.value = gasResponse?.strategy === "COVERED";
    gasFeesReason.value = gasResponse.reason;
  } catch (error) {
    errorMessage.value = t("errorMessages.default");
  }
};

const buyItem = async () => {
  if (queueTimerInterval) {
    clearInterval(queueTimerInterval);
    queueTimerInterval = undefined;
  }
  if (!props.baseMarketItem.price) return;
  trackEvent("confirm_and_pay_button", {
    itemId: `${props.baseMarketItem.id}`,
    itemCategoryId: `${props.baseMarketItem.categoryId}`,
    itemPrice: `${props.baseMarketItem.price.GFAL ?? null}`,
  });
  errorMessage.value = "";
  isProcessingPurchase.value = true;

  try {
    const purchaseRes = await initPurchase();
    purchaseCreateSuccess(purchaseRes);
  } catch (error: any | GFALResponse<Purchase>) {
    handlePurchaseError(error);
  }
};

const initPurchase = async () => {
  isModalLoaderShown.value = true;
  emit("toggleModalLock");
  try {
    const purchaseRes = await props.createPurchase(props.identifier);
    if (purchaseRes) {
      errorMessage.value = "";
    }
    return purchaseRes;
  } catch (error: any) {
    if (error.statusCode === 400) {
      emit("getQueue2FA", props.baseMarketItem);
      return error;
    }
    throw error;
  }
};

const handlePurchaseError = (error: any) => {
  switch (error.statusCode) {
    case 400:
      purchaseCreateSuccess(error);
      break;
    case 402:
      errorMessage.value = t(
        "errorMessages.userWalletInsufficientFundsTransfer"
      );
      isProcessingPurchase.value = false;
      isModalLoaderShown.value = false;
      emit("toggleModalLock");
      break;
    case 403:
      errorMessage.value = t("errorMessages.userWalletNotCreated");
      isProcessingPurchase.value = false;
      isModalLoaderShown.value = false;
      emit("toggleModalLock");
      break;
    case 404:
      handleSoldOut();
      break;
    case 409:
      if (props.minting) {
        errorMessage.value = t("errorMessages.default");
      } else {
        errorMessage.value = t("errorMessages.userUniqueItemOwned");
      }
      isProcessingPurchase.value = false;
      isModalLoaderShown.value = false;
      emit("toggleModalLock");
      emit("isPurchased");
      break;
    case 412:
      errorMessage.value = t(
        "errorMessages." +
          props.baseMarketItem.merchantId +
          ".userGameLogInRequired"
      );
      isProcessingPurchase.value = false;
      isModalLoaderShown.value = false;
      emit("toggleModalLock");
      break;
    case 418:
      handleKYCRequired(error.data);
      isModalLoaderShown.value = false;
      emit("toggleModalLock");
      break;
    case 423:
      isProcessingPurchase.value = false;
      isModalLoaderShown.value = false;
      if (error.data.payload?.position) {
        emit("toggleQueueModal", error.data.payload.position);
      } else {
        emit("toggleModalLock");
        emit("isLocked");
      }
      break;
    case 430:
      errorMessage.value = t("errorMessages.userGameRequirementsNotMet");
      isProcessingPurchase.value = false;
      isModalLoaderShown.value = false;
      emit("toggleModalLock");
      break;
    case 432:
      errorMessage.value = t("errorMessages.itemAlreadySold");
      isProcessingPurchase.value = false;
      isModalLoaderShown.value = false;
      emit("toggleModalLock");
      break;
    case 433:
      errorMessage.value = t("errorMessages.collectibleUpdated");
      isProcessingPurchase.value = false;
      isModalLoaderShown.value = false;
      emit("toggleModalLock");
      emit("isUpdated");
      break;
    case 451:
      errorMessage.value = t("errorMessages.userKYCBanned");
      isProcessingPurchase.value = false;
      isModalLoaderShown.value = false;
      emit("toggleModalLock");
      break;
    default:
      errorMessage.value = t("errorMessages.default");
      isModalLoaderShown.value = false;
      emit("toggleModalLock");
      isProcessingPurchase.value = false;
      break;
  }
};

const cancelPurchaseFromQueue = async () => {
  await cancelQueueTicket(
    props.baseMarketItem.id,
    props.baseMarketItem.merchantId
  );
  //emit("close"); do not close, show message instead
};

const purchaseCreateSuccess = (response: GFALResponse<Purchase>) => {
  if (response.payload) {
    const signPayload = {
      merchantId: props.baseMarketItem.merchantId,
      purchaseId: response.payload.id,
      operationHash: response.payload.operationHash,
    };
    emit("get2FA", signPayload, props.baseMarketItem);
  }
};

const handleKYCRequired = (response: GFALResponse<Purchase>) => {
  if (response.payload) {
    isKYCVerified.value = false;
    KYC_URI.value = response.payload.verificationURI;
    isProcessingPurchase.value = false;
  }
};

const subTotal = computed(() => {
  if (props.baseMarketItem.price)
    return roundNum(props.baseMarketItem.price.GFAL);
  toaster("error");
  return 0;
});

const subTotalUSD = computed(() => {
  if (props.baseMarketItem.price)
    return roundNum(props.baseMarketItem.price.GFAL * GFALExStore.GFALExRate);
  toaster("error");
  return 0;
});

const orderTotal = computed(() => {
  if (props.baseMarketItem.price)
    return roundNum(
      props.baseMarketItem.price.GFAL +
        (gasFeesCovered.value ? 0 : gasFeesGFAL.value)
    );
  toaster("error");
  return 0;
});

const orderTotalUSD = computed(() => {
  if (props.baseMarketItem.price)
    return roundNum(
      (props.baseMarketItem.price.GFAL +
        (gasFeesCovered.value ? 0 : gasFeesGFAL.value)) *
        GFALExStore.GFALExRate
    );
  toaster("error");
  return 0;
});
</script>
<template>
  <ModalLoader
    v-if="isModalLoaderShown"
    :title="'Purchasing Item'"
    :body-subtitle="$t('main.modalBuyBegin.loader')" />
  <div v-else>
    <div class="flex flex-col gap-2">
      <h1
        class="text-secondary-200 text-xl font-bold border-b border-b-neutral-500">
        {{
          minting
            ? $t("main.modalBuyBegin.mintingSummary")
            : $t("main.modalBuyBegin.orderSummary")
        }}
      </h1>
      <div
        class="flex items-center flex-wrap gap-2 border-b border-b-neutral-500 pb-2">
        <div class="border border-secondary-500">
          <img
            class="w-16 object-contain"
            :class="{ 'saturate-0 opacity-50': minting }"
            :src="getPortraitItemImage(baseMarketItem.imageId)"
            @error="
              (e: any) =>
                ((e.target as HTMLImageElement).src = getPortraitItemImage())
            " />
        </div>
        <div class="flex flex-col justify-center gap-1 min-h-full">
          <p class="text-neutral-200 font-bold">
            {{ $t(`${baseMarketItem.translationId}.title`) }}
            {{ $t(`${baseMarketItem.translationId}.subtitle`) }}
          </p>
          <p
            class="w-max h-6 px-2 text-xs text-neutral-300 bg-secondary-500 flex items-center">
            {{
              $t(
                `${baseMarketItem.merchantId}.property.categoryId.values.${baseMarketItem.categoryId}`
              )
            }}
          </p>
        </div>
        <div class="hidden sm:flex flex-col ml-auto min-h-full h-[52px]">
          <p class="text-neutral-300 font-bold leading-6">
            {{ subTotal }}
            GFAL
          </p>
          <p class="text-xs text-right text-secondary-200">
            ~
            {{ subTotalUSD }}
            USD
          </p>
        </div>
      </div>
      <div
        class="sm:hidden flex flex-col items-start justify-between border-b border-b-neutral-500 pb-2">
        <div class="w-full flex items-center justify-between">
          <p class="text-neutral-300 font-bold">
            {{ $t("main.modalBuyBegin.subtotal") }}
          </p>
          <p class="text-neutral-300 font-bold">
            {{ subTotal }}
            GFAL
          </p>
        </div>
        <p class="w-full text-xs text-right text-secondary-200">
          ~
          {{ subTotalUSD }}
          USD
        </p>
      </div>
      <CommonGasFees
        :is-loading="isLoading"
        :gas-fees="gasFeesGFAL"
        :covered="gasFeesCovered"
        :reason="gasFeesReason" />
      <div
        class="h-24 flex flex-col items-start justify-between pt-2 border-t border-t-neutral-500">
        <div class="w-full flex items-center justify-between">
          <p class="text-neutral-200 text-xl font-bold">
            {{ $t("main.modalBuyBegin.total") }}
          </p>
          <p v-if="!isLoading" class="text-neutral-200 font-bold text-xl">
            {{ orderTotal }}
            GFAL
          </p>
        </div>
        <p v-if="!isLoading" class="w-full text-right text-neutral-300">
          ~
          {{ orderTotalUSD }}
          USD
        </p>
        <div class="ml-auto mt-4 flex text-xs gap-1">
          <p class="text-secondary-200 text-xs">
            {{ $t("main.modalBuyBegin.quoteUpdate") }}
            <span
              >{{ remainingSeconds }}
              {{
                remainingSeconds > 1
                  ? $t("main.modalBuyBegin.seconds")
                  : $t("main.modalBuyBegin.second")
              }}</span
            >
          </p>
        </div>
      </div>

      <CommonGFALWallet
        :has-funds="
          userStore.hasSufficentBalance(baseMarketItem.price!.GFAL)
        " />

      <div
        v-if="
          !userStore.accountAddress ||
          !userStore.hasSufficentBalance(baseMarketItem.price!.GFAL) ||
          errorMessage
        "
        class="text-error-300 flex items-center gap-2">
        <NuxtIcon
          name="mdi:warning"
          :alt="$t('main.imageAltTexts.error')"
          class="w-3 h-3 shrink-0" />
        <p
          v-if="
            !userStore.accountAddress ||
            !userStore.hasSufficentBalance(baseMarketItem.price!.GFAL)
          "
          class="text-xs text-error-300"
          v-html="
            !userStore.accountAddress
              ? $t('errorMessages.userWalletNotCreated')
              : $t('errorMessages.userWalletInsufficientFunds')
          "></p>
        <p v-else class="text-xs text-error-300" v-html="errorMessage"></p>
      </div>

      <CommonKYCNotice v-if="!isKYCVerified && !errorMessage" />
    </div>
    <div
      class="mt-5 mb-2 sm:mt-6 flex items-center gap-2"
      v-if="showInfluencerReferral()">
      <CommonUserCard
        :id="userStore.referredByUser"
        size="custom"
        custom-class="w-9" />
      <p class="text-neutral-300 text-xs">
        {{ $t("main.modalBuyBegin.referredRoyalty") }}
      </p>
    </div>
    <div
      v-if="isLimitedEdition && !buyFromQueue"
      class="bg-[#2B2B2B] p-2 gap-2 my-4 flex rounded">
      <NuxtIcon
        name="mdi:information"
        class="w-4 text-rarity-exotic mt-[0.1rem]" />
      <p class="text-rarity-exotic text-sm">
        {{ $t("main.modalBuyBegin.limitedEditionNotice") }}
      </p>
    </div>

    <div v-if="buyFromQueue" class="bg-[#2B2B2B] p-2 gap-2 my-4 flex rounded">
      <NuxtIcon
        name="mdi:information"
        class="w-4 text-rarity-exotic mt-[0.1rem]" />
      <p class="text-rarity-exotic text-sm">
        <template v-if="queueRemainingSeconds > 0">
          {{ $t("main.modalBuyBegin.queueConfirmStart") }}
          <b>
            {{
              $t("main.modalBuyBegin.seconds", {
                seconds: queueRemainingSeconds,
              })
            }}
          </b>
          {{ $t("main.main.modalBuyBegin.queueConfirmEnd") }}
        </template>
        <template v-else>
          {{ $t("main.modalBuyBegin.timedOutEnd") }}
        </template>
      </p>
    </div>
    <div class="mt-5 mb-2 sm:mt-6 flex">
      <div class="flex items-center gap-4">
        <BaseButtonSubmit
          v-if="isKYCVerified"
          id="confirm_and_pay_button"
          :loading="isProcessingPurchase"
          :disabled="
            isProcessingPurchase ||
            errorMessage !== '' ||
            !userStore.hasSufficentBalance(
              baseMarketItem.price!.GFAL
            ) ||
            !userStore.accountAddress ||
            isLoading ||
            (buyFromQueue && queueRemainingSeconds <= 0)
          "
          :text="confirmButtonText"
          data-test="button-confirm-and-pay"
          :contextual-styles="true"
          @click="buyItem" />

        <a
          v-if="
            !isKYCVerified &&
            userStore.hasSufficentBalance(baseMarketItem.price!.GFAL)
          "
          id="get_verified_button"
          target="_self"
          :href="KYC_URI"
          class="h-8 rounded-md bg-primary-500 px-6 py-4 font-bold text-sm text-neutral-200 shadow-xs border-2 border-primary-900 cursor-pointer flex items-center uppercase whitespace-nowrap"
          @click="trackEvent('get_verified_button')">
          {{ $t("main.modalBuyBegin.getVerified") }}
        </a>

        <BaseButtonCancel
          id="cancel_buy_button"
          data-test="button-cancel-buy"
          type="button"
          :class="isProcessingPurchase && 'hidden'"
          :text="$t('main.modalBuyBegin.cancel')"
          :contextual-styles="true"
          @click="cancelFromButton" />
      </div>
    </div>
  </div>
</template>
